import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Badge } from "./ui/badge";
import { FileText, AlertTriangle, TrendingUp, CheckCircle, MessageSquare, Landmark, Eye, Target, DollarSign, ShoppingBag, Users } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const sectionIcons = {
  overview: Eye,
  changes: TrendingUp,
  strengths: CheckCircle,
  criticals: AlertTriangle,
  commitments: Landmark,
  bep: Target,
  cashflow: DollarSign,
  products: ShoppingBag,
  whale_curve: Users,
  talking_points: MessageSquare,
};

const sectionColors = {
  overview: 'border-l-blue-500 bg-blue-50/30',
  changes: 'border-l-indigo-500 bg-indigo-50/30',
  strengths: 'border-l-emerald-500 bg-emerald-50/30',
  criticals: 'border-l-red-500 bg-red-50/30',
  commitments: 'border-l-amber-500 bg-amber-50/30',
  bep: 'border-l-teal-500 bg-teal-50/30',
  cashflow: 'border-l-cyan-500 bg-cyan-50/30',
  products: 'border-l-orange-500 bg-orange-50/30',
  whale_curve: 'border-l-pink-500 bg-pink-50/30',
  talking_points: 'border-l-violet-500 bg-violet-50/30',
};

export const BriefingPage = ({ clientId, year, month, clientName }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/clients/${clientId}/briefing`, { params: { year, month } });
        setData(res.data);
      } catch (e) {
        if (e.response?.status !== 404) toast.error("Errore nel caricamento briefing");
        setData(null);
      } finally { setLoading(false); }
    };
    if (clientId && year && month) fetch();
  }, [clientId, year, month]);

  if (loading) return <div className="py-12 text-center text-muted-foreground">Generazione briefing...</div>;
  if (!data) return (
    <div className="py-12 text-center text-muted-foreground">
      <FileText className="h-12 w-12 mx-auto mb-3 text-slate-300" />
      <p className="text-lg font-medium mb-1">Nessun dato disponibile</p>
      <p>Carica i bilanci del periodo per generare il briefing pre-consulenza.</p>
    </div>
  );

  return (
    <div className="max-w-3xl mx-auto space-y-5" data-testid="briefing-page">
      {/* Header */}
      <div className="text-center mb-6">
        <div className="flex items-center justify-center gap-2 mb-2">
          <FileText className="h-6 w-6 text-violet-600" />
          <h2 className="text-xl font-bold tracking-tight">Briefing Pre-Consulenza</h2>
        </div>
        <p className="text-muted-foreground text-sm">{clientName || ''} — {data.period}</p>
        {data.alert_count > 0 && (
          <Badge className="mt-2 bg-red-100 text-red-800 border-red-300">{data.alert_count} criticita da discutere</Badge>
        )}
      </div>

      {/* Sections */}
      {data.sections.map((section, idx) => {
        const Icon = sectionIcons[section.type] || FileText;
        const color = sectionColors[section.type] || 'border-l-slate-500 bg-slate-50/30';

        return (
          <Card key={idx} className={`border-l-4 ${color} shadow-sm`} data-testid={`briefing-section-${section.type}`}>
            <CardHeader className="pb-2 pt-4">
              <CardTitle className="flex items-center gap-2 text-base">
                <Icon className="h-4.5 w-4.5 flex-shrink-0" />
                {section.title}
              </CardTitle>
            </CardHeader>
            <CardContent className="pb-4">
              <div className="space-y-2">
                {section.content.split('\n').map((line, i) => {
                  if (!line.trim()) return null;
                  const isAction = line.startsWith('PRIORIT') || line.startsWith('ATTENZIONE');
                  const isBullet = line.match(/^\d+\.|^-|^•/);
                  return (
                    <p key={i} className={`text-sm leading-relaxed ${
                      isAction ? 'font-bold text-red-700 bg-red-50 p-2 rounded border border-red-200' :
                      section.type === 'talking_points' ? 'pl-4 border-l-2 border-violet-200 py-0.5' :
                      section.type === 'criticals' && !line.includes('Nessuna') ? 'pl-4 border-l-2 border-red-200 py-0.5 text-red-700' :
                      section.type === 'strengths' ? 'pl-4 border-l-2 border-emerald-200 py-0.5 text-emerald-700' :
                      'text-slate-700'
                    }`}>
                      {line}
                    </p>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        );
      })}

      {/* Print hint */}
      <p className="text-center text-xs text-muted-foreground pt-4">
        Usa Ctrl+P o il pulsante Stampa per stampare questo briefing prima della consulenza.
      </p>
    </div>
  );
};

export default BriefingPage;
